import {
    Close as CloseIcon,
    Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import React from 'react';
import type { NodeProps } from 'reactflow';
import { orchestratorAgent } from '../services/orchestrator.js';
import { useCanvasStore } from '../stores/canvasStore.js';
import type { DiscoveryNode as DiscoveryNodeType } from '../types/index.js';

export const DiscoveryNode: React.FC<NodeProps<DiscoveryNodeType>> = ({ data }) => {
  const node = data;
  const [isHovered, setIsHovered] = React.useState(false);
  const [isExecuting, setIsExecuting] = React.useState(false);
  const activeCanvasId = useCanvasStore((state) => state.activeCanvasId);
  const removeDiscoveryNode = useCanvasStore((state) => state.removeDiscoveryNode);
  const activeCanvas = useCanvasStore((state) => 
    state.canvases.find(c => c.id === activeCanvasId)
  );

  const handleClick = async (e: React.MouseEvent) => {
    // Don't execute if clicking the close button
    if ((e.target as HTMLElement).closest('.close-button')) {
      return;
    }
    
    if (isExecuting) return;
    
    setIsExecuting(true);
    try {
      // Execute the prompt through the orchestrator
      const canvasState = activeCanvas ? {
          canvasId: activeCanvas.id,
          widgets: activeCanvas.widgets.map(w => ({
            id: w.id,
            type: w.type,
            title: w.config?.title || w.type,
            position: w.position,
            outputs: w.outputs
          })),
          connections: activeCanvas.connections,
          // Include discovery nodes so backend is aware of existing suggestions
          discoveryNodes: (activeCanvas.discoveryNodes || []).map(n => ({
            id: n.id,
            title: n.title,
            description: n.description,
            prompt: n.prompt,
            position: n.position,
            anchorWidgetId: n.anchorWidgetId,
            pinned: n.pinned,
          }))
        } : undefined;
        
      await orchestratorAgent.executeTask(node.prompt, {
        canvasState
      });
    } catch (error) {
      console.error('Failed to execute discovery node:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeCanvasId) {
      removeDiscoveryNode(activeCanvasId, node.id);
    }
  };

  return (
    <Paper
      elevation={isHovered ? 6 : 2}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      sx={{
        width: 180,
        minHeight: 100,
        padding: 2,
        paddingTop: 3,
        cursor: isExecuting ? 'wait' : 'pointer',
        border: '2px dashed',
        borderColor: isHovered ? 'primary.main' : 'divider',
        backgroundColor: isHovered ? 'action.hover' : 'background.paper',
        transition: 'all 0.3s ease',
        opacity: isExecuting ? 0.7 : 1,
        position: 'relative',
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: (theme) => `0 0 20px ${theme.palette.primary.main}40`,
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      {/* Close button */}
      <IconButton
        className="close-button"
        size="small"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          padding: 0.5,
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.2s',
          color: 'text.secondary',
          '&:hover': {
            color: 'error.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LightbulbIcon 
          sx={{ 
            color: 'primary.main',
            fontSize: 24,
            animation: isHovered ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.6 },
              '100%': { opacity: 1 },
            },
          }} 
        />
      </Box>
      
      <Tooltip title={node.description} placement="top" arrow>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            textAlign: 'center',
            color: 'text.primary',
          }}
        >
          {node.title}
        </Typography>
      </Tooltip>
      
      <Typography
        variant="caption"
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          fontStyle: 'italic',
          display: isHovered ? 'block' : 'none',
        }}
      >
        Click to explore
      </Typography>
    </Paper>
  );
};